// S5 — UoM settings (/catalogue/uoms, Module 01 §4 + §3.2), on the revision-6
// register. Two §3.3 DataTables (units + generic conversions) with inline
// edit; archiving a unit is guarded by usage counts (409 UOM_IN_USE → the
// reference counts, stated once in a banner). Write surfaces are hidden for
// roles without `catalogue.uom.manage` (facility_admin, group_admin); the
// server remains the authoritative gate. Every request state has the §3.9
// treatment: skeleton while loading, retry card carrying the request id on
// error, one directing sentence + the next action when empty. UoM has no cost
// surface, so there is nothing to redact here.
//
// Row-level outcomes used to be extra `<tr colSpan>` rows spliced into the
// table — invisible to the phone collapse and unreachable by the primitive.
// Each section now states its outcome ONCE, in a §3.8 banner above its table.

import { useEffect, useState } from "react";
import { useNavigate } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { Card } from "../../../../components/card";
import { PageHeader } from "../../../../components/breadcrumbs";
import { Button } from "../../../../components/button";
import { Banner } from "../../../../components/toast";
import { DataTable, type DataTableColumn } from "../../../../components/data-table";
import { EmptyState, ErrorState, TableSkeleton } from "../../../../components/states";
import { StatusBadge } from "../../../../components/status-badge";
import { StatusChip } from "../../../../components/status-chip";
import { UnauthorizedError } from "../../../../lib/problem";
import { requestIdOf } from "../../../../lib/request";
import {
  useArchiveUom,
  useCreateUom,
  useCreateUomConversion,
  useDeleteUomConversion,
  useUomConversions,
  useUoms,
  useUpdateUom,
} from "../hooks/catalogue-queries";
import { useCatalogueCaps } from "./uom-templates/caps";
import {
  activeUomsOfDimension,
  DIMENSIONS,
  hasActiveBase,
  messageFor,
  parseUomInUse,
  uomCode,
  visibleUoms,
  type Uom,
  type UomConversion,
  type UomDimension,
} from "./uom-templates/helpers";
import "../catalogue.css";

export function UomSettingsScreen() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const caps = useCatalogueCaps();
  const uomsQuery = useUoms();
  const conversionsQuery = useUomConversions();
  const [showArchived, setShowArchived] = useState(false);
  const [addingUnit, setAddingUnit] = useState(false);

  const unauthorized =
    uomsQuery.error instanceof UnauthorizedError ||
    conversionsQuery.error instanceof UnauthorizedError;
  useEffect(() => {
    if (unauthorized) void navigate({ to: "/login" });
  }, [unauthorized, navigate]);

  const head = (
    <>
      <PageHeader
        title={t("catalogue.uom.title")}
        crumbs={[{ label: t("nav.catalogue"), to: "/catalogue/products" }]}
        actions={
          caps.canManageUoms && !addingUnit ? (
            <Button
              variant="primary"
              data-testid="add-unit"
              onClick={() => {
                setAddingUnit(true);
              }}
            >
              {t("catalogue.uom.addUnit")}
            </Button>
          ) : undefined
        }
      />
      <p className="cat-note">{t("catalogue.uom.subtitle")}</p>
    </>
  );

  if (uomsQuery.error && !unauthorized) {
    return (
      <div className="cat-screen">
        {head}
        <ErrorState
          title={t("catalogue.state.errorTitle")}
          detail={messageFor(uomsQuery.error, t("catalogue.state.errorDetail"))}
          retryLabel={t("catalogue.state.retry")}
          onRetry={() => void uomsQuery.refetch()}
          requestId={requestIdOf(uomsQuery.error)}
          requestIdLabel={t("catalogue.state.requestId")}
        />
      </div>
    );
  }

  const allUoms = uomsQuery.data?.items ?? [];

  /* ---- the §5 zero-CLS gate ------------------------------------------------
   * Two stacked Cards, and the SECOND one's position is decided by the FIRST
   * one's row count. Letting the units table swap its skeleton for real rows
   * while the conversions Card is already on the glass drops that Card down the
   * page — measured 0.0619 on the replica, the largest shift in the area. Both
   * reads settle before either Card paints, and the reveal remounts. */
  const ready = uomsQuery.isFetched && conversionsQuery.isFetched;
  if (!ready) {
    return (
      <div className="cat-screen" key="uoms-loading">
        {head}
        <Card title={t("catalogue.uom.unitsTitle")} data-testid="uoms-loading">
          <TableSkeleton rows={6} cols={7} label={t("catalogue.state.loading")} />
        </Card>
        <Card title={t("catalogue.uom.conversionsTitle")}>
          <TableSkeleton rows={3} cols={4} label={t("catalogue.state.loading")} />
        </Card>
      </div>
    );
  }

  return (
    <div className="cat-screen" key="uoms-ready">
      {head}

      <UnitsSection
        uoms={allUoms}
        loading={uomsQuery.isPending || unauthorized}
        showArchived={showArchived}
        onShowArchived={setShowArchived}
        canManage={caps.canManageUoms}
        adding={addingUnit}
        onAdd={() => {
          setAddingUnit(true);
        }}
        onAddDone={() => {
          setAddingUnit(false);
        }}
      />

      <ConversionsSection
        uoms={allUoms}
        conversions={conversionsQuery.data?.items ?? []}
        isPending={conversionsQuery.isPending}
        error={conversionsQuery.error}
        onRetry={() => void conversionsQuery.refetch()}
        canManage={caps.canManageUoms}
      />
    </div>
  );
}

// ---- Units -----------------------------------------------------------------

function UnitsSection({
  uoms,
  loading,
  showArchived,
  onShowArchived,
  canManage,
  adding,
  onAdd,
  onAddDone,
}: {
  uoms: Uom[];
  loading: boolean;
  showArchived: boolean;
  onShowArchived: (next: boolean) => void;
  canManage: boolean;
  adding: boolean;
  onAdd: () => void;
  onAddDone: () => void;
}) {
  const { t } = useTranslation();
  const [editingId, setEditingId] = useState<string | null>(null);
  const [notice, setNotice] = useState<{ kind: "ok" | "bad"; text: string } | null>(null);
  const rows = visibleUoms(uoms, showArchived);

  const columns: DataTableColumn<Uom>[] = [
    { key: "code", header: t("catalogue.uom.colCode"), id: true },
    { key: "name", header: t("catalogue.uom.colName") },
    {
      key: "dimension",
      header: t("catalogue.uom.colDimension"),
      render: (u) => t(`catalogue.uom.dimension.${u.dimension}`),
    },
    {
      key: "base",
      header: t("catalogue.uom.colBase"),
      render: (u) =>
        u.is_base ? (
          <StatusChip variant="accent" dot={false}>
            {t("catalogue.uom.baseBadge")}
          </StatusChip>
        ) : (
          <span className="cat-note">—</span>
        ),
    },
    { key: "decimal_precision", header: t("catalogue.uom.colPrecision"), numeric: true },
    {
      key: "source",
      header: t("catalogue.uom.colSource"),
      render: (u) => t(`catalogue.uom.source.${u.source}`),
    },
    {
      key: "status",
      header: t("catalogue.uom.colStatus"),
      render: (u) => <StatusBadge status={u.status} />,
    },
    ...(canManage
      ? [
          {
            key: "actions",
            header: t("catalogue.uom.colActions"),
            actions: true,
            render: (u: Uom) => (
              <UnitRowActions
                uom={u}
                onEdit={() => {
                  setNotice(null);
                  setEditingId(u.id);
                }}
                onNotice={setNotice}
              />
            ),
          },
        ]
      : []),
  ];

  const editing = rows.find((u) => u.id === editingId);

  return (
    <Card
      title={t("catalogue.uom.unitsTitle")}
      count={rows.length}
      data-testid="uom-units"
      action={
        <label className="cat-check cat-fld">
          <input
            type="checkbox"
            checked={showArchived}
            onChange={(e) => {
              onShowArchived(e.target.checked);
            }}
          />
          <span>{t("catalogue.uom.showArchived")}</span>
        </label>
      }
    >
      {notice !== null ? (
        <div className="ds-cpad">
          <Banner
            kind={notice.kind}
            data-testid="uom-result"
            dismissLabel={t("catalogue.state.dismiss")}
            onDismiss={() => {
              setNotice(null);
            }}
          >
            {notice.text}
          </Banner>
        </div>
      ) : null}

      {canManage && adding ? (
        <AddUnitForm
          uoms={uoms}
          onDone={(created) => {
            if (created !== undefined) {
              setNotice({ kind: "ok", text: t("catalogue.uom.unitCreated", { code: created }) });
            }
            onAddDone();
          }}
        />
      ) : null}

      {canManage && editing !== undefined ? (
        <EditUnitForm
          uom={editing}
          onDone={(saved) => {
            if (saved) {
              setNotice({ kind: "ok", text: t("catalogue.uom.unitSaved", { code: editing.code }) });
            }
            setEditingId(null);
          }}
        />
      ) : null}

      <DataTable
        columns={columns}
        rows={rows}
        rowKey={(u) => u.id}
        rowTestId="uom-row"
        caption={t("catalogue.uom.unitsTitle")}
        loading={loading}
        loadingLabel={t("catalogue.state.loading")}
        empty={
          <EmptyState
            title={t("catalogue.uom.unitsEmptyTitle")}
            body={
              uoms.length > 0
                ? t("catalogue.uom.archivedHiddenBody")
                : t("catalogue.uom.unitsEmptyBody")
            }
            ctaLabel={
              uoms.length > 0
                ? t("catalogue.uom.showArchived")
                : canManage
                  ? t("catalogue.uom.addUnit")
                  : undefined
            }
            onCta={
              uoms.length > 0
                ? () => {
                    onShowArchived(true);
                  }
                : canManage
                  ? onAdd
                  : undefined
            }
          />
        }
      />
    </Card>
  );
}

function UnitRowActions({
  uom,
  onEdit,
  onNotice,
}: {
  uom: Uom;
  onEdit: () => void;
  onNotice: (n: { kind: "ok" | "bad"; text: string }) => void;
}) {
  const { t } = useTranslation();
  const archive = useArchiveUom(uom.id);

  /* INLINE ROW ACTIONS ARE ACCENT LINKS (§3.3), not buttons — and certainly
   * not thirteen red ones. Danger styling belongs to the CONFIRMATION of a
   * destructive act, not to its offer: a units table where nothing is wrong
   * must show almost no colour (§1 rule 3). */
  return (
    <span className="rte-row-actions">
      <button type="button" className="ds-lnk" onClick={onEdit}>
        {t("catalogue.uom.edit")}
      </button>
      {uom.status === "active" ? (
        <button
          type="button"
          className="ds-lnk"
          disabled={archive.isPending}
          onClick={() => {
            archive.mutate(undefined, {
              onSuccess: () => {
                onNotice({ kind: "ok", text: t("catalogue.uom.unitArchived", { code: uom.code }) });
              },
              onError: (e) => {
                const inUse = parseUomInUse(e);
                onNotice({
                  kind: "bad",
                  text: inUse
                    ? t("catalogue.uom.inUse", {
                        products: inUse.products,
                        bomLines: inUse.bomLines,
                      })
                    : t("catalogue.uom.archiveFailed"),
                });
              },
            });
          }}
        >
          {t("catalogue.uom.archive")}
        </button>
      ) : null}
    </span>
  );
}

function EditUnitForm({ uom, onDone }: { uom: Uom; onDone: (saved: boolean) => void }) {
  const { t } = useTranslation();
  const update = useUpdateUom(uom.id);
  const [name, setName] = useState(uom.name);
  const [precision, setPrecision] = useState(String(uom.decimal_precision));

  return (
    <form
      className="cat-form"
      data-testid="uom-edit-form"
      onSubmit={(e) => {
        e.preventDefault();
        update.mutate(
          { name: name.trim(), decimal_precision: Number.parseInt(precision, 10) },
          {
            onSuccess: () => {
              onDone(true);
            },
          },
        );
      }}
    >
      <div className="cat-fld">
        <label htmlFor="uomt-edit-name">
          {t("catalogue.uom.nameLabel")} · {uom.code}
        </label>
        <input
          id="uomt-edit-name"
          value={name}
          onChange={(e) => {
            setName(e.target.value);
          }}
          required
        />
      </div>
      <div className="cat-fld">
        <label htmlFor="uomt-edit-prec">{t("catalogue.uom.precisionLabel")}</label>
        <input
          id="uomt-edit-prec"
          type="number"
          min={0}
          max={6}
          step={1}
          value={precision}
          onChange={(e) => {
            setPrecision(e.target.value);
          }}
        />
      </div>
      <div className="cat-form-actions">
        <Button
          type="button"
          onClick={() => {
            onDone(false);
          }}
        >
          {t("catalogue.uom.cancel")}
        </Button>
        <Button
          type="submit"
          variant="primary"
          busy={update.isPending}
          disabled={name.trim().length === 0}
        >
          {t("catalogue.uom.save")}
        </Button>
      </div>
      {update.isError ? (
        <div className="cat-form-wide">
          <Banner kind="bad">{messageFor(update.error, t("catalogue.uom.updateFailed"))}</Banner>
        </div>
      ) : null}
    </form>
  );
}

function AddUnitForm({
  uoms,
  onDone,
}: {
  uoms: Uom[];
  onDone: (createdCode: string | undefined) => void;
}) {
  const { t } = useTranslation();
  const create = useCreateUom();
  const [code, setCode] = useState("");
  const [name, setName] = useState("");
  const [dimension, setDimension] = useState<UomDimension>("length");
  const [precision, setPrecision] = useState("3");
  const [isBase, setIsBase] = useState(false);

  const forcedBase = !hasActiveBase(uoms, dimension);
  const effectiveBase = forcedBase || isBase;
  const canSubmit = code.trim().length > 0 && name.trim().length > 0 && !create.isPending;

  return (
    <form
      className="cat-form"
      data-testid="uom-add-form"
      onSubmit={(e) => {
        e.preventDefault();
        if (!canSubmit) return;
        const trimmed = code.trim();
        create.mutate(
          {
            code: trimmed,
            name: name.trim(),
            dimension,
            decimal_precision: Number.parseInt(precision, 10),
            is_base: effectiveBase,
          },
          {
            onSuccess: () => {
              onDone(trimmed);
            },
          },
        );
      }}
    >
      <div className="cat-fld">
        <label htmlFor="uomt-new-code">{t("catalogue.uom.codeLabel")}</label>
        <input
          id="uomt-new-code"
          value={code}
          onChange={(e) => {
            setCode(e.target.value);
          }}
          required
        />
      </div>
      <div className="cat-fld">
        <label htmlFor="uomt-new-name">{t("catalogue.uom.nameLabel")}</label>
        <input
          id="uomt-new-name"
          value={name}
          onChange={(e) => {
            setName(e.target.value);
          }}
          required
        />
      </div>
      <div className="cat-fld">
        <label htmlFor="uomt-new-dim">{t("catalogue.uom.dimensionLabel")}</label>
        <select
          id="uomt-new-dim"
          value={dimension}
          onChange={(e) => {
            setDimension(e.target.value as UomDimension);
          }}
        >
          {DIMENSIONS.map((d) => (
            <option key={d} value={d}>
              {t(`catalogue.uom.dimension.${d}`)}
            </option>
          ))}
        </select>
      </div>
      <div className="cat-fld">
        <label htmlFor="uomt-new-prec">{t("catalogue.uom.precisionLabel")}</label>
        <input
          id="uomt-new-prec"
          type="number"
          min={0}
          max={6}
          step={1}
          value={precision}
          onChange={(e) => {
            setPrecision(e.target.value);
          }}
        />
      </div>
      <div className="cat-fld cat-check">
        <input
          id="uomt-new-base"
          type="checkbox"
          checked={effectiveBase}
          disabled={forcedBase}
          onChange={(e) => {
            setIsBase(e.target.checked);
          }}
        />
        <label htmlFor="uomt-new-base">{t("catalogue.uom.baseLabel")}</label>
      </div>
      <div className="cat-form-actions">
        <Button
          type="button"
          onClick={() => {
            onDone(undefined);
          }}
        >
          {t("catalogue.uom.cancel")}
        </Button>
        <Button type="submit" variant="primary" busy={create.isPending} disabled={!canSubmit}>
          {t("catalogue.uom.save")}
        </Button>
      </div>
      {forcedBase ? <p className="cat-hint cat-form-wide">{t("catalogue.uom.baseAuto")}</p> : null}
      {create.isError ? (
        <div className="cat-form-wide">
          <Banner kind="bad">{messageFor(create.error, t("catalogue.uom.createFailed"))}</Banner>
        </div>
      ) : null}
    </form>
  );
}

// ---- Conversions -----------------------------------------------------------

function ConversionsSection({
  uoms,
  conversions,
  isPending,
  error,
  onRetry,
  canManage,
}: {
  uoms: Uom[];
  conversions: UomConversion[];
  isPending: boolean;
  error: unknown;
  onRetry: () => void;
  canManage: boolean;
}) {
  const { t } = useTranslation();
  const [adding, setAdding] = useState(false);
  const del = useDeleteUomConversion();
  const [notice, setNotice] = useState<{ kind: "ok" | "bad"; text: string } | null>(null);

  const columns: DataTableColumn<UomConversion>[] = [
    {
      key: "from",
      header: t("catalogue.uom.colFrom"),
      id: true,
      render: (c) => uomCode(uoms, c.from_uom_id) ?? c.from_uom_id,
    },
    {
      key: "to",
      header: t("catalogue.uom.colTo"),
      render: (c) => uomCode(uoms, c.to_uom_id) ?? c.to_uom_id,
    },
    { key: "factor", header: t("catalogue.uom.colFactor"), numeric: true },
    ...(canManage
      ? [
          {
            key: "actions",
            header: t("catalogue.uom.colActions"),
            actions: true,
            render: (c: UomConversion) => (
              <button
                type="button"
                className="ds-lnk"
                disabled={del.isPending}
                onClick={() => {
                  setNotice(null);
                  del.mutate(c.id, {
                    onSuccess: () => {
                      setNotice({ kind: "ok", text: t("catalogue.uom.conversionRemoved") });
                    },
                    onError: (e) => {
                      setNotice({
                        kind: "bad",
                        text: messageFor(e, t("catalogue.uom.conversionDeleteFailed")),
                      });
                    },
                  });
                }}
              >
                {t("catalogue.uom.remove")}
              </button>
            ),
          },
        ]
      : []),
  ];

  return (
    <Card
      title={t("catalogue.uom.conversionsTitle")}
      count={conversions.length}
      data-testid="uom-conversions"
      action={
        canManage && !adding ? (
          <Button
            size="sm"
            data-testid="add-conversion"
            onClick={() => {
              setAdding(true);
            }}
          >
            {t("catalogue.uom.addConversion")}
          </Button>
        ) : undefined
      }
    >
      {notice !== null ? (
        <div className="ds-cpad">
          <Banner
            kind={notice.kind}
            data-testid="conversion-result"
            dismissLabel={t("catalogue.state.dismiss")}
            onDismiss={() => {
              setNotice(null);
            }}
          >
            {notice.text}
          </Banner>
        </div>
      ) : null}

      {canManage && adding ? (
        <AddConversionForm
          uoms={uoms}
          onDone={(created) => {
            if (created) setNotice({ kind: "ok", text: t("catalogue.uom.conversionCreated") });
            setAdding(false);
          }}
        />
      ) : null}

      <DataTable
        columns={columns}
        rows={conversions}
        rowKey={(c) => c.id}
        rowTestId="conversion-row"
        caption={t("catalogue.uom.conversionsTitle")}
        loading={isPending}
        loadingLabel={t("catalogue.state.loading")}
        error={
          error !== null && error !== undefined ? (
            <ErrorState
              title={t("catalogue.state.errorTitle")}
              detail={messageFor(error, t("catalogue.state.errorDetail"))}
              retryLabel={t("catalogue.state.retry")}
              onRetry={onRetry}
              requestId={requestIdOf(error)}
              requestIdLabel={t("catalogue.state.requestId")}
            />
          ) : undefined
        }
        empty={
          <EmptyState
            title={t("catalogue.uom.conversionsEmptyTitle")}
            body={t("catalogue.uom.conversionsEmptyBody")}
            ctaLabel={canManage ? t("catalogue.uom.addConversion") : undefined}
            onCta={
              canManage
                ? () => {
                    setAdding(true);
                  }
                : undefined
            }
          />
        }
      />
    </Card>
  );
}

function AddConversionForm({ uoms, onDone }: { uoms: Uom[]; onDone: (created: boolean) => void }) {
  const { t } = useTranslation();
  const create = useCreateUomConversion();
  const [fromId, setFromId] = useState("");
  const [toId, setToId] = useState("");
  const [factor, setFactor] = useState("");

  const active = uoms.filter((u) => u.status === "active");
  const fromUom = active.find((u) => u.id === fromId);
  const toOptions = fromUom
    ? activeUomsOfDimension(uoms, fromUom.dimension).filter((u) => u.id !== fromId)
    : [];
  const factorNum = Number.parseFloat(factor);
  const canSubmit =
    fromId.length > 0 &&
    toId.length > 0 &&
    Number.isFinite(factorNum) &&
    factorNum > 0 &&
    !create.isPending;

  return (
    <form
      className="cat-form"
      data-testid="conversion-add-form"
      onSubmit={(e) => {
        e.preventDefault();
        if (!canSubmit) return;
        create.mutate(
          { from_uom_id: fromId, to_uom_id: toId, factor: factorNum },
          {
            onSuccess: () => {
              onDone(true);
            },
          },
        );
      }}
    >
      <div className="cat-fld">
        <label htmlFor="uomt-conv-from">{t("catalogue.uom.fromLabel")}</label>
        <select
          id="uomt-conv-from"
          value={fromId}
          onChange={(e) => {
            setFromId(e.target.value);
            setToId("");
          }}
          required
        >
          <option value="" disabled>
            {t("catalogue.uom.selectPlaceholder")}
          </option>
          {active.map((u) => (
            <option key={u.id} value={u.id}>
              {u.code}
            </option>
          ))}
        </select>
      </div>
      <div className="cat-fld">
        <label htmlFor="uomt-conv-to">{t("catalogue.uom.toLabel")}</label>
        <select
          id="uomt-conv-to"
          value={toId}
          onChange={(e) => {
            setToId(e.target.value);
          }}
          disabled={fromUom === undefined}
          required
        >
          <option value="" disabled>
            {t("catalogue.uom.selectPlaceholder")}
          </option>
          {toOptions.map((u) => (
            <option key={u.id} value={u.id}>
              {u.code}
            </option>
          ))}
        </select>
      </div>
      <div className="cat-fld">
        <label htmlFor="uomt-conv-factor">{t("catalogue.uom.factorLabel")}</label>
        <input
          id="uomt-conv-factor"
          type="number"
          min={0}
          step="any"
          value={factor}
          onChange={(e) => {
            setFactor(e.target.value);
          }}
          required
        />
      </div>
      <div className="cat-form-actions">
        <Button
          type="button"
          onClick={() => {
            onDone(false);
          }}
        >
          {t("catalogue.uom.cancel")}
        </Button>
        <Button type="submit" variant="primary" busy={create.isPending} disabled={!canSubmit}>
          {t("catalogue.uom.save")}
        </Button>
      </div>
      <p className="cat-hint cat-form-wide">{t("catalogue.uom.factorHint")}</p>
      {fromUom && toOptions.length === 0 ? (
        <p className="cat-hint cat-form-wide">{t("catalogue.uom.sameDimensionHint")}</p>
      ) : null}
      {create.isError ? (
        <div className="cat-form-wide">
          <Banner kind="bad">
            {messageFor(create.error, t("catalogue.uom.conversionCreateFailed"))}
          </Banner>
        </div>
      ) : null}
    </form>
  );
}
